import type { QueryClient } from '@tanstack/react-query';
import { nanoid } from 'nanoid';

import type { DraftAdapter, DraftData, PetitionDraft } from '../types';

export class InMemoryAdapter implements DraftAdapter {
	private readonly queryClient: QueryClient;
	private readonly queryKeyPrefix = 'draft';
	private readonly queryOptions = {
		gcTime: 600_000,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	};

	constructor(queryClient: QueryClient) {
		this.queryClient = queryClient;
	}

	async createDraft(authorId: string, data?: DraftData): Promise<PetitionDraft> {
		try {
			const newDraft: PetitionDraft = { ...data, id: nanoid(), authorId, status: 'active', updatedAt: Date.now() };
			this.queryClient.setQueryDefaults([this.queryKeyPrefix, authorId], this.queryOptions);
			this.queryClient.setQueryData([this.queryKeyPrefix, authorId], newDraft);
			return newDraft;
		} catch (error: unknown) {
			throw error instanceof Error ? error : new Error(String(error));
		}
	}

	async getDraft(authorId: string): Promise<PetitionDraft | null> {
		const draft = this.queryClient.getQueryData<PetitionDraft>([this.queryKeyPrefix, authorId]);
		return draft ?? null;
	}

	async updateDraft(authorId: string, updates: DraftData): Promise<PetitionDraft> {
		const existingDraft = await this.getDraft(authorId);
		if (!existingDraft) throw new Error('Draft not found');

		const updatedDraft: PetitionDraft = { ...existingDraft, ...updates, updatedAt: Date.now() };
		this.queryClient.setQueryData([this.queryKeyPrefix, authorId], updatedDraft);
		return updatedDraft;
	}

	async findOrCreateDraft(authorId: string, data?: DraftData): Promise<PetitionDraft> {
		const existingDraft = await this.getDraft(authorId);
		if (existingDraft) return existingDraft;

		return this.createDraft(authorId, data);
	}
}
